import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, apiUrl, endpoints, type BoardMember, type PersonaPreset } from '../api/client'
import { Avatar } from '../components/Avatar'
import { parseAmount } from '../amounts'

/*
  The setup run.

  Five steps in the order the work actually happens: say what the business is, look at who
  is going to argue about it, hand over a statement, then the figures you know off the top
  of your head, then the ones you have to look up. It opens once, on the first sign-in,
  and never again.

  ── Every step can be skipped, and skipping is not free ─────────────────────

  An owner reaching this at 7am in a van cannot upload a profit and loss statement, and a
  wizard that will not let them past is a wizard they close. So every step has Skip. What
  skipping does is write the step onto Actions as an ordinary commitment with a date on
  it, which is the machinery this product already uses to chase things the owner said they
  would do. A skipped step is not lost; it is Friday's problem.

  ── Nothing here is stored as progress ──────────────────────────────────────

  Which steps are done is worked out from the data every time this renders: the profile
  decides basics, the figures decide the two number steps, the cabinet decides the
  statement. The only thing the server records is that the owner has been walked through
  at all (migration 042). An owner who filled their revenue in from the Company screen last
  week sees that step already ticked, because it is.

  ── Why the board step does not tune anything ───────────────────────────────

  Signup already built five seats, choosing the second by trade. What this step is for is
  the owner seeing them and swapping any that read wrong, which is a decision they can make
  in four seconds a seat. Moving nineteen sliders is not, and The Board screen already does
  it properly with the sentence updating as you go. So this offers a swap and a link, and
  the tuning happens where tuning belongs.
*/

type StepKey = 'basics' | 'board' | 'statement' | 'easy' | 'detail'

interface Company {
  name: string
  trade: string
  timezone: string
  headcount: number | null
  annualRevenue: number | null
  ebitda: number | null
  fiscalYearEnd: string | null
  website: string | null
  gbpUrl: string | null
  founderSummary: string | null
  meetingDow: number
  meetingHour: number
  documents: { id: string; extractionStatus: string }[]
  completeness: number
}

interface Figure { metric: string }

/*
  Two names per step, because the rail and the heading want different things.

  `short` is a noun and fits five-across inside a 640px card at any width. `title` is the
  sentence the owner reads once they are on the step. Using the sentence in both clipped
  the fifth label off the end of the rail, and a progress bar with a step you cannot read
  is worse than one with terse labels.
*/
const STEPS: { key: StepKey; short: string; title: string; lede: string }[] = [
  {
    key: 'basics',
    short: 'Business',
    title: 'What the business is',
    lede: 'The board reads this before every meeting. What is missing here is what it has to guess at.',
  },
  {
    key: 'board',
    short: 'Board',
    title: 'Who is going to argue',
    lede: 'Five seats, picked for your trade. Swap any that do not read like somebody you would want in the room.',
  },
  {
    key: 'statement',
    short: 'Statement',
    title: 'A financial statement',
    lede: 'One profit and loss statement teaches the board more than an hour of typing. A photograph of one is fine.',
  },
  {
    key: 'easy',
    short: 'Numbers',
    title: 'The numbers you already know',
    lede: 'No looking anything up. If you do not know one off the top of your head, leave it.',
  },
  {
    key: 'detail',
    short: 'Detail',
    title: 'The ones worth looking up',
    lede: 'These are the two that change what the board tells you to do, and both usually need checking.',
  },
]

export function Onboarding({ onDone }: { onDone: () => void }) {
  const [at, setAt] = useState(0)
  const [company, setCompany] = useState<Company | null>(null)
  const [board, setBoard] = useState<BoardMember[]>([])
  const [figures, setFigures] = useState<Figure[]>([])
  const [error, setError] = useState<string | null>(null)
  const [finishing, setFinishing] = useState(false)

  const reload = useCallback(async () => {
    const [c, b, n] = await Promise.all([
      api.get<Company>('/api/company'),
      endpoints.board(),
      api.get<{ lastComplete: Figure[]; current: Figure[]; other: Figure[] }>('/api/numbers'),
    ])
    setCompany(c)
    setBoard(b)
    setFigures([...n.lastComplete, ...n.current, ...n.other])
  }, [])

  useEffect(() => { void reload().catch(() => setError('Could not load your account.')) }, [reload])

  /*
    A statement is read by the worker after the upload returns, so its figures land a few
    seconds after this screen last looked. Without checking again, "Cash on hand" stayed
    unticked beside a cash figure the board already had, and the next step asked for a
    number the owner had just handed over. Re-read while anything is still being read,
    and give up after two minutes rather than polling a stuck file for ever.
  */
  const reading = company?.documents.some(d => d.extractionStatus === 'pending') ?? false
  useEffect(() => {
    if (!reading) return
    const started = Date.now()
    const id = setInterval(() => {
      if (Date.now() - started > 120_000) { clearInterval(id); return }
      void reload().catch(() => { /* the next tick tries again */ })
    }, 3000)
    return () => clearInterval(id)
  }, [reading, reload])

  const has = useCallback(
    (metric: string) => figures.some(f => f.metric === metric),
    [figures])

  /*
    Done-ness, asked of the data rather than remembered.

    'board' is the one that cannot be derived: a seat the owner looked at and kept is
    identical to a seat they never opened. So it counts as done once they press the button,
    which is what the button says.
  */
  const [boardSeen, setBoardSeen] = useState(false)

  const done = useMemo<Record<StepKey, boolean>>(() => ({
    basics:    Boolean(company && company.name && company.trade && company.headcount !== null
                 && company.annualRevenue !== null && company.fiscalYearEnd),
    board:     boardSeen,
    statement: Boolean(company && company.documents.length > 0),
    easy:      has('cash_on_hand') || has('revenue'),
    detail:    has('close_rate_pct') || has('marketing_spend'),
  }), [company, boardSeen, has])

  const step = STEPS[at]
  const last = at === STEPS.length - 1

  const remember = async (key: StepKey) => {
    // Idempotent on the server: skipping the same step twice leaves one open commitment,
    // so this can be called freely without checking what is already on the list.
    try { await api.post<void>('/api/onboarding/skip', { step: key }) } catch { /* below */ }
  }

  /*
    Finishing writes down whatever is still outstanding.

    Not only what was explicitly skipped. Continue on a step the owner did not fill in is
    the commonest way through this screen and it used to record nothing at all, so an owner
    who clicked through every step arrived at an empty dashboard with no idea what was
    missing. That is the exact outcome the skip mechanism exists to prevent, and leaving it
    to the Skip button meant it only worked for people who pressed the button that says
    they are not doing the work.
  */
  const finish = async () => {
    setFinishing(true)
    try {
      await Promise.all(STEPS.filter(s => !done[s.key]).map(s => remember(s.key)))
      await api.post<void>('/api/onboarding/complete')
    } catch {
      // Not worth stranding somebody in a wizard over. The worst case is being offered it
      // once more on the next sign-in.
    }
    onDone()
  }

  const skip = async () => {
    await remember(step.key)
    if (last) await finish(); else setAt(a => a + 1)
  }

  const next = async () => { if (last) await finish(); else setAt(a => a + 1) }

  if (!company) {
    return (
      <div className="ob">
        <div className="ob__card">
          {/* A failed load has to say so. The skeleton alone waits forever. */}
          {error
            ? <div className="signin__error">{error}</div>
            : <div className="skeleton" style={{ width: '60%' }} />}
        </div>
      </div>
    )
  }

  return (
    <div className="ob">
      <div className="ob__card">
        <header className="ob__head">
          <ol className="ob__rail" aria-label="Setup progress">
            {STEPS.map((s, i) => (
              <li
                key={s.key}
                className={`ob__tick${i === at ? ' is-at' : ''}${done[s.key] ? ' is-done' : ''}`}
                aria-current={i === at ? 'step' : undefined}
              >
                <span className="ob__tickmark" aria-hidden>{done[s.key] ? '✓' : i + 1}</span>
                <span className="ob__ticklabel">{s.short}</span>
              </li>
            ))}
          </ol>
        </header>

        <div className="ob__body">
          <h1 className="ob__title">{step.title}</h1>
          <p className="ob__lede">{step.lede}</p>

          {error && <div className="signin__error">{error}</div>}

          {step.key === 'basics' && (
            <Basics company={company} onSaved={reload} />
          )}
          {step.key === 'board' && (
            <Board board={board} trade={company.trade} onSwapped={reload} />
          )}
          {step.key === 'statement' && (
            <Statement count={company.documents.length} onUploaded={reload} />
          )}
          {step.key === 'easy' && (
            <Figures
              fields={[
                { metric: 'cash_on_hand', label: 'Cash on hand', unit: 'usd',
                  why: 'Decides how long you can be wrong for.' },
                { metric: 'revenue', label: 'Revenue this year so far', unit: 'usd',
                  why: 'Everything else the board says is a proportion of it.' },
              ]}
              has={has}
              onSaved={reload}
            />
          )}
          {step.key === 'detail' && (
            <Figures
              fields={[
                { metric: 'close_rate_pct', label: 'Close rate', unit: 'percent',
                  why: 'Quotes won over quotes given. Tells you whether the problem is leads or pricing.' },
                /*
                  Year to date, not per month, and that is not a slip.

                  Every figure in this product is stored against a period with a shape, and
                  the two on offer are this year so far and last year (FiscalCalendar). A
                  monthly number filed under a yearly period is the exact mistake the shape
                  rules exist to stop: the board would set it beside annual revenue and
                  conclude marketing is a rounding error. Ask for the year, get a figure
                  that can be reasoned about.
                */
                { metric: 'marketing_spend', label: 'Marketing spend this year so far', unit: 'usd',
                  why: 'The board cannot tell you whether leads are expensive without it.' },
              ]}
              has={has}
              onSaved={reload}
            />
          )}
        </div>

        <footer className="ob__foot">
          <button className="btn btn--quiet" onClick={() => void skip()} disabled={finishing}>
            {done[step.key] ? 'Not now' : 'Skip, remind me'}
          </button>

          <div className="ob__go">
            {at > 0 && (
              <button className="btn" onClick={() => setAt(a => a - 1)} disabled={finishing}>
                Back
              </button>
            )}
            <button
              className="btn btn--primary"
              onClick={() => { if (step.key === 'board') setBoardSeen(true); void next() }}
              disabled={finishing}
            >
              {last ? (finishing ? 'Finishing' : 'Done') : 'Continue'}
            </button>
          </div>
        </footer>

        {/* Skipping the lot. Honest about what it costs, and it still writes every
            outstanding step onto Actions rather than dropping them. */}
        {/* Finishing already writes down everything outstanding, so leaving early and
            walking to the end have the same consequence. This is a shortcut, not a
            different decision. */}
        <button
          className="ob__bail linkish"
          onClick={() => void finish()}
          disabled={finishing}
        >
          I'll do all this later
        </button>
      </div>
    </div>
  )
}

/* ── Step 1: the basics ───────────────────────────────────────────────────── */

function Basics({ company, onSaved }: { company: Company; onSaved: () => Promise<void> }) {
  const [form, setForm] = useState({
    name: company.name ?? '',
    trade: company.trade ?? '',
    headcount: company.headcount?.toString() ?? '',
    annualRevenue: company.annualRevenue?.toString() ?? '',
    ebitda: company.ebitda?.toString() ?? '',
    fiscalYearEnd: company.fiscalYearEnd ?? '',
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    const headcount = parseAmount(form.headcount)
    const annualRevenue = parseAmount(form.annualRevenue)
    const ebitda = parseAmount(form.ebitda)
    const unread = [
      Number.isNaN(headcount) && 'Employees',
      Number.isNaN(annualRevenue) && 'Annual revenue',
      Number.isNaN(ebitda) && 'EBITDA',
    ].filter(Boolean)
    if (unread.length) {
      setError(`${unread.join(' and ')}: that is not a number. Try 1,850,000 or 1.85m.`)
      return
    }

    setBusy(true)
    setError(null)
    try {
      await api.put<void>('/api/company', {
        ...company,
        name: form.name.trim(),
        trade: form.trade.trim(),
        headcount,
        annualRevenue,
        ebitda,
        fiscalYearEnd: form.fiscalYearEnd || null,
      })
      await onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ob__form">
      <Field label="Company name" value={form.name}
             onChange={v => setForm(f => ({ ...f, name: v }))} />
      <Field label="Trade" value={form.trade} placeholder="plumbing"
             hint="What the business actually does. It picks the seats and the numbers the board asks for."
             onChange={v => setForm(f => ({ ...f, trade: v }))} />
      <Field label="Employees" value={form.headcount} inputMode="numeric"
             onChange={v => setForm(f => ({ ...f, headcount: v }))} />
      <Field label="Annual revenue" value={form.annualRevenue} inputMode="numeric" prefix="$"
             hint="Last full year."
             onChange={v => setForm(f => ({ ...f, annualRevenue: v }))} />
      <Field label="EBITDA" value={form.ebitda} inputMode="numeric" prefix="$"
             hint="Negative is fine and worth saying."
             onChange={v => setForm(f => ({ ...f, ebitda: v }))} />
      <Field label="Fiscal year ends" value={form.fiscalYearEnd} type="date"
             hint="Decides what counts as this year, which decides nearly every figure."
             onChange={v => setForm(f => ({ ...f, fiscalYearEnd: v }))} />

      {error && <div className="signin__error">{error}</div>}

      <button className="btn" onClick={() => void save()} disabled={busy}>
        {busy ? 'Saving' : 'Save these'}
      </button>
    </div>
  )
}

function Field({
  label, value, onChange, hint, placeholder, prefix, type, inputMode,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  hint?: string
  placeholder?: string
  prefix?: string
  type?: string
  inputMode?: 'numeric'
}) {
  return (
    <label className="ob__field">
      <span className="ob__label">{label}</span>
      <span className="ob__input">
        {prefix && <span className="ob__affix">{prefix}</span>}
        <input
          className="input"
          type={type ?? 'text'}
          inputMode={inputMode}
          value={value}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value)}
        />
      </span>
      {hint && <span className="ob__hint">{hint}</span>}
    </label>
  )
}

/* ── Step 2: the board ────────────────────────────────────────────────────── */

function Board({
  board, trade, onSwapped,
}: { board: BoardMember[]; trade: string; onSwapped: () => Promise<void> }) {
  const [picking, setPicking] = useState<BoardMember | null>(null)
  const [presets, setPresets] = useState<PersonaPreset[]>([])
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!picking || presets.length) return
    void endpoints.presets().then(setPresets).catch(() => setPresets([]))
  }, [picking, presets.length])

  const apply = async (seat: BoardMember, preset: PersonaPreset) => {
    setBusy(true)
    try {
      await endpoints.saveSeat(seat.id, {
        displayName: preset.displayName,
        archetype: preset.archetype || null,
        backstory: preset.backstory ?? null,
        customPrompt: null,
        domains: preset.domains,
        attributes: preset.attributes,
        // The face travels with the persona, the same as it does in the editor: a seat
        // re-cast as somebody else that kept the old portrait reads as a bug.
        avatarUrl: preset.avatarUrl,
      })
      await onSwapped()
      setPicking(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ob__board">
      {trade && <p className="ob__trade">Picked for a {trade} business.</p>}

      <ul className="ob__seats">
        {board.map(seat => (
          <li key={seat.id} className="ob__seat">
            <Avatar member={seat} size="lg" />
            <span className="ob__seatwho">
              <span className="ob__seatname">{seat.displayName}</span>
              <span className="ob__seatrole">
                {seat.archetype}
                {seat.seatRole === 'chair' && ' · chairs the meeting'}
                {seat.seatRole === 'dissenter' && ' · argues the other side'}
              </span>
            </span>
            <button className="btn btn--small" onClick={() => setPicking(seat)} disabled={busy}>
              Swap
            </button>
          </li>
        ))}
      </ul>

      <p className="ob__aside">
        Each seat can be tuned in detail later on <strong>The Board</strong>: how much
        cushion it wants, how hard it holds a position, what it is deep in.
      </p>

      {picking && (
        <div className="ob__presets" role="dialog" aria-label={`Replace ${picking.displayName}`}>
          <div className="ob__presethead">
            <strong>Replace {picking.displayName}</strong>
            <button className="linkish" onClick={() => setPicking(null)}>Cancel</button>
          </div>
          <ul>
            {presets.map(p => (
              <li key={p.key}>
                <button className="ob__preset" onClick={() => void apply(picking, p)} disabled={busy}>
                  {p.avatarUrl && <img src={p.avatarUrl} alt="" loading="lazy" />}
                  <span>
                    <span className="ob__seatname">{p.displayName}</span>
                    <span className="ob__seatrole">{p.archetype}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/* ── Step 3: a statement ──────────────────────────────────────────────────── */

function Statement({ count, onUploaded }: { count: number; onUploaded: () => Promise<void> }) {
  const input = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const send = async (file: File) => {
    setBusy(true)
    setError(null)
    try {
      const body = new FormData()
      body.append('file', file)
      body.append('kind', 'profit_loss')
      const res = await fetch(apiUrl('/api/company/documents'), {
        method: 'POST', body, credentials: 'include',
      })
      if (!res.ok) throw new Error(await res.text() || 'Upload failed')
      await onUploaded()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not upload that')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ob__upload">
      <input
        ref={input}
        type="file"
        hidden
        accept=".pdf,.xls,.xlsx,.xlsm,.csv,.doc,.docx,.docm,.txt,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif"
        onChange={e => { const f = e.target.files?.[0]; if (f) void send(f) }}
      />

      <button className="btn btn--primary" onClick={() => input.current?.click()} disabled={busy}>
        {busy ? 'Reading it' : count > 0 ? 'Add another' : 'Choose a file'}
      </button>

      {count > 0 && (
        <p className="ob__got">
          {count} {count === 1 ? 'file is' : 'files are'} on file. The figures land in
          {' '}<strong>The Numbers</strong> once they have been read.
        </p>
      )}

      {error && <div className="signin__error">{error}</div>}

      <p className="ob__hint">
        PDF, Excel, CSV, Word, or a photograph. An iPhone photo saved as HEIC has to be
        converted to JPEG first, which is a setting on the phone's own camera.
      </p>
    </div>
  )
}

/* ── Steps 4 and 5: figures ───────────────────────────────────────────────── */

interface FigureField {
  metric: string
  label: string
  unit: 'usd' | 'percent'
  why: string
}

function Figures({
  fields, has, onSaved,
}: {
  fields: FigureField[]
  has: (metric: string) => boolean
  onSaved: () => Promise<void>
}) {
  return (
    <div className="ob__figures">
      {fields.map(f => <FigureRow key={f.metric} field={f} on={has(f.metric)} onSaved={onSaved} />)}
    </div>
  )
}

function FigureRow({
  field, on, onSaved,
}: { field: FigureField; on: boolean; onSaved: () => Promise<void> }) {
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    const n = parseAmount(value)
    if (n === null) return
    if (Number.isNaN(n)) { setError('That is not a number. Try 42,000 or 42k.'); return }
    setBusy(true)
    setError(null)
    try {
      await api.post<void>('/api/numbers', {
        metric: field.metric,
        label: field.label,
        value: n,
        unit: field.unit,
        period: 'ytd',
      })
      setValue('')
      await onSaved()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save that')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`ob__figure${on ? ' is-on' : ''}`}>
      <span className="ob__figurehead">
        <span className="ob__label">{field.label}</span>
        {on && <span className="ob__on">on file</span>}
      </span>
      <span className="ob__hint">{field.why}</span>

      <form
        className="ob__figureform"
        onSubmit={e => { e.preventDefault(); void save() }}
      >
        <span className="ob__input">
          {field.unit === 'usd' && <span className="ob__affix">$</span>}
          <input
            className="input"
            inputMode="decimal"
            value={value}
            placeholder={on ? 'Replace it' : field.unit === 'percent' ? '40' : '12,400'}
            onChange={e => setValue(e.target.value)}
          />
          {field.unit === 'percent' && <span className="ob__affix">%</span>}
        </span>
        <button className="btn btn--small" disabled={busy || !value.trim()}>
          {busy ? 'Saving' : 'Add'}
        </button>
      </form>

      {error && <div className="signin__error">{error}</div>}
    </div>
  )
}
